/**
 * useCustomerCreditHistory
 *
 * Hook que busca o histórico vitalício de créditos/débitos do cliente via RPC
 * `get_customer_credit_history`. Agrega eventos imutáveis de TODAS as
 * assinaturas + snapshot atual de `financeiro_creditos`. Sobrevive à exclusão
 * de cobranças (a fonte é `financeiro_assinatura_eventos`).
 *
 * RELEASE: 2026-06-22 v1.0.0 - Versão inicial.
 */

import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { createLogger } from '@/lib/logger';
import type { CobrancaProRataItemDiff } from './useCobrancaCalculationMemory';

const log = createLogger('useCustomerCreditHistory');

export interface CustomerCreditEvent {
  id: string;
  created_at: string;
  event_type: string;
  event_source: string | null;
  actor_full_name: string | null;
  ciclo_numero: number | null;
  assinatura: { id: string; frequencia: string | null; valor_total: number | null } | null;
  cobranca: {
    id: string;
    existe: boolean;
    valor_total: number | null;
    status: string | null;
  } | null;
  ciclo_origem: number | null;
  factor: number | null;
  delta_total: number | null;
  credito_total: number;
  debito_total: number;
  items_diff: CobrancaProRataItemDiff[];
  creditos_consumidos: number | null;
  credit_id: string | null;
  change_diff: any;
  metadata: any;
}

export interface CustomerCreditBalance {
  id: string;
  origem: string;
  valor: number;
  valor_usado: number;
  saldo: number;
  status: string;
  expira_em: string | null;
  assinatura_id: string | null;
  created_at: string;
  /** v1.1.0 — metadata do crédito (motivo/autor de cancelamento manual, etc.) */
  metadata?: Record<string, any> | null;
}

export interface CustomerCreditHistory {
  customer: { id: string; nome: string };
  resumo: {
    total_creditos_gerados: number;
    total_debitos_gerados: number;
    total_creditos_aplicados: number;
    saldo_credito_disponivel: number;
    total_eventos: number;
    total_assinaturas_envolvidas: number;
  };
  saldos: CustomerCreditBalance[];
  eventos: CustomerCreditEvent[];
  paginacao: { total: number; limit: number; offset: number };
}

export interface UseCustomerCreditHistoryParams {
  customerId: string | null | undefined;
  enabled?: boolean;
  limit?: number;
  offset?: number;
  eventTypes?: string[] | null;
  periodStart?: string | null;
  periodEnd?: string | null;
}

export function useCustomerCreditHistory({
  customerId,
  enabled = true,
  limit = 100,
  offset = 0,
  eventTypes = null,
  periodStart = null,
  periodEnd = null,
}: UseCustomerCreditHistoryParams) {
  return useQuery<CustomerCreditHistory>({
    queryKey: [
      'customer-credit-history',
      customerId,
      limit,
      offset,
      eventTypes,
      periodStart,
      periodEnd,
    ],
    enabled: !!customerId && enabled,
    staleTime: 30_000,
    queryFn: async () => {
      const { data, error } = await supabase.rpc('get_customer_credit_history', {
        p_customer_id: customerId as string,
        p_limit: limit,
        p_offset: offset,
        p_event_types: eventTypes,
        p_period_start: periodStart,
        p_period_end: periodEnd,
      });
      if (error) {
        log.error('Falha ao carregar histórico de créditos do cliente', error);
        throw error;
      }
      return data as unknown as CustomerCreditHistory;
    },
  });
}
